import React, { useRef, useEffect, ReactNode } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Animated,
  Easing,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Svg, Path } from 'react-native-svg';

type Props = {
  visible: boolean;
  query: string;
  onChangeQuery: (text: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  placeholder: string;
  cancelLabel: string;
  recentSearches: string[];
  onRecentSearchPress: (search: string) => void;
  minQueryLength?: number;
  children?: ReactNode;
  // Optional filter chip bar rendered directly below the search input.
  // The host page owns its state; the overlay just paints it.
  filterBar?: ReactNode;
  // When true, results (`children`) render even when the user hasn't typed
  // enough characters yet — used so an active filter still shows results.
  filtersActive?: boolean;
};

export default function SearchOverlay({
  visible,
  query,
  onChangeQuery,
  onSubmit,
  onCancel,
  placeholder,
  cancelLabel,
  recentSearches,
  onRecentSearchPress,
  minQueryLength = 3,
  children,
  filterBar,
  filtersActive = false,
}: Props) {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(-60)).current;
  const headerOpacity = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      slideAnim.setValue(-60);
      headerOpacity.setValue(0);
      contentOpacity.setValue(0);
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 260,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.timing(headerOpacity, {
            toValue: 1,
            duration: 260,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
        ]),
        Animated.timing(contentOpacity, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handlePressCancel = () => {
    Animated.sequence([
      Animated.timing(contentOpacity, {
        toValue: 0,
        duration: 140,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: -60,
          duration: 200,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(headerOpacity, {
          toValue: 0,
          duration: 200,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]),
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 180,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(() => onCancel());
  };

  if (!visible) return null;

  return (
    <Animated.View style={[styles.overlay, { opacity: fadeAnim }]}>
      <SafeAreaView style={styles.container} edges={['top']}>
        <Animated.View
          style={[
            styles.header,
            { opacity: headerOpacity, transform: [{ translateY: slideAnim }] },
          ]}
        >
          <View style={styles.inputContainer}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.6324 3C16.2814 3 20.0499 6.76847 20.0499 11.4175C20.0499 16.0666 16.2814 19.836 11.6324 19.836C6.98331 19.836 3.21484 16.0666 3.21484 11.4175C3.21484 6.76847 6.98331 3 11.6324 3Z"
                stroke="#151515"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M17.3594 17.584L20.7844 21.0002"
                stroke="#151515"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
            <TextInput
              style={styles.input}
              placeholder={placeholder}
              placeholderTextColor="#A19F97"
              value={query}
              onChangeText={onChangeQuery}
              onSubmitEditing={onSubmit}
              autoFocus
            />
          </View>
          <TouchableOpacity onPress={handlePressCancel}>
            <Text style={styles.cancelText}>{cancelLabel}</Text>
          </TouchableOpacity>
        </Animated.View>

        {filterBar ? (
          <Animated.View style={{ opacity: headerOpacity, width: '100%' }}>
            {filterBar}
          </Animated.View>
        ) : null}

        <Animated.View style={{ opacity: contentOpacity, flex: 1 }}>
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {query.length < minQueryLength && !filtersActive && recentSearches.length > 0 && (
              <View style={styles.recentSection}>
                <View style={styles.recentList}>
                  {recentSearches.map((search, index) => (
                    <TouchableOpacity
                      key={index}
                      style={styles.recentItem}
                      onPress={() => onRecentSearchPress(search)}
                    >
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path
                          d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z"
                          stroke="#A19F97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <Path
                          d="M8 4v4l2 2"
                          stroke="#A19F97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Svg>
                      <Text style={styles.recentText}>{search}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            {(query.length >= minQueryLength || filtersActive) && children}
          </ScrollView>
        </Animated.View>
      </SafeAreaView>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  container: {
    backgroundColor: '#F9F8F3',
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    backgroundColor: '#F9F8F3',
    gap: 12,
  },
  inputContainer: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    height: 48,
    gap: 12,
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    padding: 0,
    margin: 0,
  },
  cancelText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  content: {
    height: '100%',
    paddingHorizontal: 24,
    backgroundColor: '#F9F8F3',
  },
  recentSection: {
    paddingTop: 16,
  },
  recentList: {
    gap: 12,
  },
  recentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F0EEE4',
    borderRadius: 16,
  },
  recentText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
  },
});
